/**
 * Former Mover Acquisition Framework — data source.
 *
 * Each entry powers a /former/{slug} successor page. Acquired (closed) moving
 * company domains 301 to their entry here so backlink equity consolidates to
 * a single, relevant, honest landing page on LifeTime Movers.
 *
 * To add a new acquired domain: append one entry, deploy, then configure the
 * Cloudflare 301 per FORMER-MOVER-PLAYBOOK.md.
 */

export interface FormerMover {
  slug: string;
  oldName: string;
  oldDomain: string;
  /** Vague phrasing is fine if exact dates unknown. */
  yearsServed: string;
  city: string;
  serviceType: "local" | "long-distance" | "local-and-long-distance";
  citiesServed: string[];
  /** Short paragraph: who they were, that they've closed. Honest, factual. */
  acknowledgment: string;
  /** 3–5 contextual internal links to LTM pages. */
  internalLinks: { label: string; href: string }[];
  /** ISO date for sitemap lastmod. */
  lastUpdated: string;
  /** Approximate dissolution date (ISO YYYY-MM-DD) for Organization schema. */
  dissolutionDate: string;
}

export const formerMovers: FormerMover[] = [
  {
    slug: "long-distance-moving-companies-usa",
    oldName: "Long Distance Moving Companies USA",
    oldDomain: "longdistancemovingcompaniesusa.com",
    yearsServed: "served the Chicago metro and nationwide long-distance market for years before closing",
    city: "Chicago metro",
    serviceType: "long-distance",
    citiesServed: [
      "Chicago, IL",
      "Oak Brook, IL",
      "Naperville, IL",
      "Schaumburg, IL",
      "Evanston, IL",
      "Aurora, IL",
      "Joliet, IL",
      "Elgin, IL",
    ],
    acknowledgment:
      "Long Distance Moving Companies USA served Chicago-area households and businesses relocating across state lines for years before ceasing operations. Former customers and anyone searching for the company today can rely on LifeTime Movers — a licensed, insured, locally-operated mover covering the same Chicago metro origin and delivering to all 48 continental states.",
    internalLinks: [
      { label: "Long Distance Moving", href: "/moving-services/long-distance-move" },
      { label: "Interstate Moving from Illinois", href: "/moving-services/interstate-move" },
      { label: "Cross-Country Moving", href: "/moving-services/cross-country" },
      { label: "Get a Free Long-Distance Quote", href: "/quote" },
      { label: "Service Areas We Cover", href: "/service-areas" },
    ],
    lastUpdated: new Date().toISOString().slice(0, 10),
    dissolutionDate: "2024-12-31",
  },
  {
    slug: "chicago-movers-choice",
    oldName: "Chicago Movers Choice",
    oldDomain: "chicagomoverschoice.com",
    yearsServed: "served Chicago and the surrounding suburbs for years before closing",
    city: "Chicago",
    serviceType: "local-and-long-distance",
    citiesServed: [
      "Chicago, IL",
      "Oak Brook, IL",
      "Naperville, IL",
      "Schaumburg, IL",
      "Hinsdale, IL",
      "Downers Grove, IL",
      "Elmhurst, IL",
      "Lombard, IL",
      "Oak Park, IL",
      "Evanston, IL",
    ],
    acknowledgment:
      "Chicago Movers Choice operated as a Chicago-area moving company serving local Illinois moves and outbound long-distance relocations before ceasing operations. Former customers searching for them today can turn to LifeTime Movers — an independent, family-operated mover based in Oak Brook covering the same Chicago neighborhoods and suburbs with the same local and long-distance service mix.",
    internalLinks: [
      { label: "Local Moving in Illinois", href: "/moving-services/local-move" },
      { label: "Long Distance Moving", href: "/moving-services/long-distance-move" },
      { label: "Chicago Movers", href: "/service-areas/movers/chicago" },
      { label: "Service Areas", href: "/service-areas" },
      { label: "Get a Free Quote", href: "/quote" },
    ],
    lastUpdated: new Date().toISOString().slice(0, 10),
    dissolutionDate: "2024-12-31",
  },
  {
    slug: "imover",
    oldName: "iMover",
    oldDomain: "imover.net",
    yearsServed: "served customers nationwide with interstate and long-distance moving services for years before ceasing operations",
    city: "nationwide",
    serviceType: "long-distance",
    citiesServed: [
      "Chicago, IL",
      "New York, NY",
      "Los Angeles, CA",
      "Dallas, TX",
      "Atlanta, GA",
      "Miami, FL",
      "Phoenix, AZ",
      "Denver, CO",
    ],
    acknowledgment:
      "iMover (imover.net) operated as a long-distance and interstate moving resource serving households relocating across the United States before ceasing operations. Former customers and visitors searching for iMover today can rely on LifeTime Movers — a licensed, insured, family-operated carrier handling interstate moves from Illinois, Indiana, Wisconsin, and Michigan to all 48 continental states, with transparent guaranteed quotes and no third-party handoffs.",
    internalLinks: [
      { label: "Interstate Moving", href: "/moving-services/interstate-move" },
      { label: "Long Distance Moving", href: "/moving-services/long-distance-move" },
      { label: "Cross-Country Moving", href: "/moving-services/cross-country" },
      { label: "National Moving Services", href: "/moving-services/national-moving" },
      { label: "Get a Free Long-Distance Quote", href: "/quote" },
    ],
    lastUpdated: new Date().toISOString().slice(0, 10),
    dissolutionDate: "2024-12-31",
  },
];

export const getFormerMover = (slug: string): FormerMover | undefined =>
  formerMovers.find((m) => m.slug === slug);
